import { Edit, Business, Person } from '@mui/icons-material';
import {
  Autocomplete,
  Button,
  createFilterOptions,
  FilterOptionsState,
  FormControl,
  Grid2,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { BankAccount, Contact, CreateInvoiceInput, Customer } from '@thro-types/billing';
import { ShibUser } from '@thro-types/users';

import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetCustomersListQuery } from 'api/hooks/billing/customer';
import { UserSelectionModal } from 'app/components/UserSelectionModal';
import { InfoCustomerCard } from 'app/containers/Billing/components';
import { translations } from 'locales/i18n';

interface ExpertContact extends Pick<Contact, 'firstName' | 'lastName' | 'email' | 'uid'> {}

export const CreateBody = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<CreateInvoiceInput & { customer: Customer }>();
  const [filterString, setFilterString] = useState<string>('');

  const { data: customers, isLoading } = useGetCustomersListQuery(filterString.length > 0);

  const [showUserSelectionModal, setShowUserSelectionModal] = useState(false);

  const handleUserSelection = (user: ShibUser) => {
    const expertContact: ExpertContact = {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      uid: user.uid
    };

    setFieldValue('expertContact', expertContact);
    setShowUserSelectionModal(false);
  };

  useEffect(() => {
    if (customers?.data) {
      setFieldValue(
        'customer',
        customers?.data.find(customer => customer.customerId === values.customerVersionId)
      );
    }
  }, [customers, values.customerVersionId, setFieldValue]);

  const filterOptions = <T extends Object>(options: T[], state: FilterOptionsState<T>) => {
    const OPTIONS_LIMIT = filterString.length < 3 ? 20 : undefined;
    const defaultFilterOptions = createFilterOptions<T>({
      stringify: option => Object.values(option).join(' ')
    });
    return defaultFilterOptions(options, state).slice(0, OPTIONS_LIMIT);
  };

  useEffect(() => {
    if (values.customer) {
      setFieldValue('customerVersionId', values.customer.customerId);
    }
  }, [values.customer, setFieldValue]);

  const getOptionLabel = (
    param: React.HTMLAttributes<HTMLLIElement> & {
      key: any;
    },
    customer: Customer
  ) => {
    const { key, ...rest } = param;
    if (customer.companyName && customer.ustId) {
      return (
        <MenuItem {...rest} key={key}>
          <Business sx={{ mr: 1 }} />
          {customer.companyName}
        </MenuItem>
      );
    }

    return (
      <MenuItem {...rest} key={key}>
        <Person sx={{ mr: 1 }} />
        {`${customer.firstName} ${customer.lastName}`}
      </MenuItem>
    );
  };

  return (
    <Grid2 container gap={2} justifyContent={'space-between'} marginBottom={2} size={{ xs: 12 }}>
      <Grid2 container gap={1} flexDirection={'column'}>
        <Typography variant="h6">{t(translations.common.customer())}</Typography>
        <Autocomplete
          filterOptions={filterOptions}
          options={customers?.data || []}
          loading={isLoading}
          renderOption={getOptionLabel}
          getOptionLabel={customer =>
            customer.companyName
              ? customer.companyName
              : `${customer.firstName} ${customer.lastName}`
          }
          getOptionKey={customer => customer.id}
          renderInput={params => <TextField {...params} />}
          size="small"
          onInputChange={(event, value) => setFilterString(value)}
          onChange={(event, value) => {
            setFieldValue('customer', value);
          }}
          style={{ minWidth: '20em' }}
          noOptionsText={
            filterString
              ? t(translations.pages.billing.noOptions())
              : t(translations.pages.billing.typeToSearch())
          }
        />
        <Typography variant="h6">{t(translations.pages.billing.detail.expertContact())}</Typography>
        <FormControl size="small">
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setShowUserSelectionModal(true)}
            endIcon={<Edit />}
            sx={{ justifyContent: 'space-between', opacity: 0.7 }}
          >
            {values.expertContact
              ? `${values.expertContact?.firstName} ${values.expertContact?.lastName}`
              : t(translations.common.select())}
          </Button>
          <UserSelectionModal
            open={showUserSelectionModal}
            setOpen={() => setShowUserSelectionModal(false)}
            handleSubmit={handleUserSelection}
            hideMtrNumberField
            hideUidField
          />
        </FormControl>
        <Typography variant="h6">
          {t(translations.pages.billing.invoiceCreate.invoiceDate())}
        </Typography>
        <DatePicker
          renderInput={params => <TextField {...params} size="small" />}
          value={values.invoiceDate}
          onChange={date => setFieldValue('invoiceDate', date)}
          inputFormat="dd.MM.yyyy"
        />
        <Typography variant="h6">{t(translations.pages.billing.detail.details())}</Typography>
        <Grid2 container gap={2}>
          <FormControl size="small" fullWidth>
            <InputLabel>{t(translations.common.bankAccount())}</InputLabel>
            <Select
              label={t(translations.common.bankAccount())}
              value={values.bankAccount}
              onChange={e => setFieldValue('bankAccount', e.target.value)}
            >
              <MenuItem value={BankAccount.STOK}>
                {t(
                  translations.pages.billing.invoiceCreate.bankAccount[
                    BankAccount.STOK.toLocaleLowerCase()
                  ]()
                )}
              </MenuItem>
              <MenuItem value={BankAccount.SPARKASSE}>
                {t(
                  translations.pages.billing.invoiceCreate.bankAccount[
                    BankAccount.SPARKASSE.toLocaleLowerCase()
                  ]()
                )}
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" fullWidth>
            <InputLabel>{t(translations.common.paymentTarget())}</InputLabel>
            <Select
              label={t(translations.common.paymentTarget())}
              value={values.paymentTarget || ''}
              onChange={e => setFieldValue('paymentTarget', e.target.value)}
            >
              <MenuItem value="0d">
                {t(translations.pages.billing.detail.paymentTargetNow())}
              </MenuItem>
              <MenuItem value="14d">
                {t(translations.pages.billing.detail.paymentTarget(), { target: '14' })}
              </MenuItem>
              <MenuItem value="30d">
                {t(translations.pages.billing.detail.paymentTarget(), { target: '30' })}
              </MenuItem>
              <MenuItem value="60d">
                {t(translations.pages.billing.detail.paymentTarget(), { target: '60' })}
              </MenuItem>
              <MenuItem value="90d">
                {t(translations.pages.billing.detail.paymentTarget(), { target: '90' })}
              </MenuItem>
            </Select>
          </FormControl>
        </Grid2>
      </Grid2>
      {values.customer && (
        <Grid2 size={{ xs: 6, md: 5, sm: 4, xl: 3 }}>
          <InfoCustomerCard data={values.customer} hideEdit />
        </Grid2>
      )}
    </Grid2>
  );
};
